<template>
  <div class="app-budget-content">
    <!-- Report Header -->
    <div class="app-header">
      <h2 class="app-title">{{ $t('reports.budgetConsumption.title') }}</h2>
      <div class="app-actions">
        <Button 
          :label="$t('common.refresh')" 
          icon="pi pi-refresh" 
          @click="loadReport"
          :loading="loading"
          class="app-button app-button--medium app-button--outline"
        />
        <Button 
          :label="$t('reports.exportPDF')" 
          icon="pi pi-file-pdf" 
          @click="exportPDF"
          :disabled="!report"
          class="app-button app-button--medium app-button--danger"
        />
        <Button 
          :label="$t('reports.exportExcel')" 
          icon="pi pi-file-excel" 
          @click="exportExcel"
          :disabled="!report"
          class="app-button app-button--medium app-button--success"
        />
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="app-loading">
      <ProgressSpinner />
      <p>{{ $t('common.loading') }}...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="app-error">
      <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
      <p>{{ error }}</p>
    </div>

    <!-- Report Content -->
    <div v-else-if="report" class="app-report">
      <!-- Summary Header -->
      <h3 class="app-section-title">
        <i class="pi pi-chart-pie" aria-hidden="true"></i>
        {{ $t('reports.budgetConsumption.overallSummary') }}
      </h3>
      
      <!-- Summary Grid -->
      <div class="app-summary-grid">
        <div class="app-summary-item">
          <span class="app-summary-item__label">{{ $t('reports.totalBudget') }}</span>
          <span class="app-summary-item__value app-summary-item__value--budget">{{ formattedTotalBudget }}</span>
        </div>
        <div class="app-summary-item">
          <span class="app-summary-item__label">{{ $t('reports.totalConsumed') }}</span>
          <span class="app-summary-item__value app-summary-item__value--consumed">{{ formattedTotalConsumed }}</span>
        </div>
        <div class="app-summary-item">
          <span class="app-summary-item__label">{{ $t('reports.totalPaid') }}</span>
          <span class="app-summary-item__value app-summary-item__value--paid">{{ formattedTotalPayments }}</span>
        </div>
        <div class="app-summary-item">
          <span class="app-summary-item__label">{{ $t('reports.budget.remaining') }}</span>
          <span class="app-summary-item__value app-summary-item__value--negative">
            {{ formattedRemainingBalance }}
          </span>
        </div>
      </div>
      
      <!-- Progress Bars -->
      <div class="app-progress-item">
        <div class="app-progress-header">
          <span class="app-progress-title">{{ $t('reports.budgetProgress') }}</span>
          <span class="app-progress-percentage">{{ formatPercentage(report.consumptionPercentage) }}</span>
        </div>
        <ProgressBar 
          :value="report.consumptionPercentage" 
          :class="getProgressBarClass(report.consumptionPercentage)"
        />
      </div>

      <div class="app-progress-item">
        <div class="app-progress-header">
          <span class="app-progress-title">{{ $t('reports.paymentProgress') }}</span>
          <span class="app-progress-percentage">{{ formatPercentage(getPaymentPercentage(report)) }}</span>
        </div>
        <ProgressBar 
          :value="getPaymentPercentage(report)" 
          :class="getPaymentProgressBarClass(getPaymentPercentage(report))"
        />
      </div>

      <!-- Detailed Reports -->
      <div class="app-details">
        <div class="app-tabs">
          <button 
            class="app-tab"
            :class="{ 'app-tab--active': activeTab === 'intervenants' }"
            @click="activeTab = 'intervenants'"
          >
            {{ $t('reports.intervenantsBudget') }}
          </button>
          <button 
            class="app-tab"
            :class="{ 'app-tab--active': activeTab === 'tasks' }"
            @click="activeTab = 'tasks'"
          >
            {{ $t('reports.tasksBudget') }}
          </button>
        </div>
        <IntervenantBudgetReport 
          v-if="activeTab === 'intervenants'"
          :intervenants="report.intervenantBudgetReports" 
          @intervenant-click="onIntervenantClick" 
        />
        <TaskBudgetReport 
          v-if="activeTab === 'tasks'"
          :tasks="report.taskBudgetReports" 
          @task-click="onTaskClick" 
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import reportService from '@/services/report.service.index';
import { formatPercentage } from '@/utils/formatters';
import { formatCurrencyWithUserPreference } from '@/utils/currencyUtils';
import TaskBudgetReport from './TaskBudgetReport.vue';
import IntervenantBudgetReport from './IntervenantBudgetReport.vue';
import Button from 'primevue/button';
import { useI18n } from 'vue-i18n';
import { useNotifications } from '@/composables/useNotifications';
import { ProgressSpinner, ProgressBar } from 'primevue';
// @ts-ignore
import { ref, onMounted, watch } from 'vue';

interface Props {
  projectId: string | number;
}

const props = defineProps<Props>();

const { t } = useI18n();
const { notifyError } = useNotifications();

const emit = defineEmits(['task-selected', 'intervenant-selected']);

// State
const report = ref<any | null>(null);
const loading = ref<boolean>(false);
const error = ref<string | null>(null);
const activeTab = ref<string>('intervenants');

// Formatted currency values
const formattedTotalBudget = ref('-');
const formattedTotalConsumed = ref('-');
const formattedTotalPayments = ref('-');
const formattedRemainingBalance = ref('-');

// Lifecycle hooks
onMounted(async () => {
  await loadReport();
});

// Methods
const loadReport = async () => {
  loading.value = true;
  error.value = null;
  
  try {
    const response = await reportService.getBudgetConsumptionReport(props.projectId);
    report.value = response;
  } catch (err) {
    console.error('Error loading budget consumption report:', err);
    error.value = t('reports.errorLoading');
  } finally {
    loading.value = false;
  }
};

const exportPDF = async () => {
  try {
    const blob = await reportService.exportBudgetReportPDF(props.projectId);
    const url = window.URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob]));
    const link = document.createElement('a');
    link.href = url;
    link.download = `budget-report-${props.projectId}-${Date.now()}.pdf`;
    link.click();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Error exporting PDF:', err);
    const e: any = err;
    notifyError(t('reports.exportError'));
  }
};

const exportExcel = async () => {
  try {
    const blob = await reportService.exportBudgetReportExcel(props.projectId);
    const url = window.URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob]));
    const link = document.createElement('a');
    link.href = url;
    link.download = `budget-report-${props.projectId}-${Date.now()}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Error exporting Excel:', err);
    const e: any = err;
    notifyError(t('reports.exportError'));
  }
};

const onTaskClick = (task: any) => {
  emit('task-selected', task);
};

const onIntervenantClick = (intervenant: any) => {
  emit('intervenant-selected', intervenant);
};

const getProgressBarClass = (percentage: number) => {
  if (percentage >= 100) return 'app-progress--over';
  if (percentage >= 80) return 'app-progress--high';
  if (percentage >= 60) return 'app-progress--medium';
  return 'app-progress--low';
};

const getPaymentPercentage = (r: any) => {
  if (!r || !r.totalBudget || r.totalBudget === 0) return 0;
  const paymentAmount = r.totalPayments || 0;
  return Math.round((paymentAmount / r.totalBudget) * 100 * 100) / 100;
};

const getPaymentProgressBarClass = (percentage: number) => {
  if (percentage >= 100) return 'app-progress--payment-complete';
  if (percentage >= 80) return 'app-progress--payment-high';
  if (percentage >= 60) return 'app-progress--payment-medium';
  return 'app-progress--payment-low';
};

// Update formatted values when report changes
watch(report, async (newReport: any) => {
  if (newReport) {
    formattedTotalBudget.value = await formatCurrencyWithUserPreference(newReport.totalBudget);
    formattedTotalConsumed.value = await formatCurrencyWithUserPreference(newReport.totalConsumed);
    formattedTotalPayments.value = await formatCurrencyWithUserPreference(newReport.totalPayments || 0);
    formattedRemainingBalance.value = await formatCurrencyWithUserPreference((newReport.totalBudget - newReport.totalPayments) || 0);
  }
}, { immediate: true });
</script>

<style scoped>
/* ==========================================================================
   BUDGET CONSUMPTION REPORT COMPONENT
   ========================================================================== */

/* Main Container */
.app-budget-content {
  padding: var(--space-4);
}

/* Header */
.app-header {
  margin-bottom: var(--space-6);
  padding-bottom: var(--space-4);
  border-bottom: 1px solid var(--neutral-200);
}

.app-title {
  margin: 0 0 var(--space-4) 0;
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--neutral-800);
  text-align: center;
}

.app-actions {
  display: flex;
  gap: var(--space-3);
  justify-content: center;
  flex-wrap: wrap;
}

/* Loading State */
.app-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-12);
  gap: var(--space-4);
  text-align: center;
}

.app-loading p {
  color: var(--neutral-600);
  margin: 0;
}

/* Error State */
.app-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-12);
  gap: var(--space-4);
  text-align: center;
}

.app-error i {
  font-size: var(--font-size-3xl);
  color: var(--error);
}

.app-error p {
  color: var(--error-dark);
  margin: 0;
}

/* Section Title */
.app-section-title {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin: 0 0 var(--space-6) 0;
  padding: 0;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--neutral-800);
}

.app-section-title i {
  color: var(--info);
  font-size: var(--font-size-xl);
}

/* Summary Grid */
.app-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-4);
  margin-bottom: var(--space-8);
}

@media (max-width: 768px) {
  .app-summary-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .app-summary-grid {
    grid-template-columns: 1fr;
  }
}

.app-summary-item {
  padding: var(--space-4);
  background: var(--neutral-50);
  border-radius: var(--radius-md);
  border: 1px solid var(--neutral-200);
  transition: all var(--transition-base);
}

.app-summary-item:hover {
  background: white;
  box-shadow: var(--shadow-sm);
  transform: translateY(-1px);
}

.app-summary-item__label {
  display: block;
  font-size: var(--font-size-xs);
  color: var(--neutral-500);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: var(--space-3);
}

.app-summary-item__value {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  line-height: 1.2;
}

.app-summary-item__value--budget {
  color: var(--info);
}

.app-summary-item__value--consumed {
  color: var(--warning);
}

.app-summary-item__value--paid {
  color: var(--success);
}

.app-summary-item__value--negative {
  color: var(--error);
}

.app-summary-item__value--percentage.app-summary-item__value--over-budget {
  color: var(--error);
  animation: pulse 2s infinite;
}

.app-summary-item__value--percentage.app-summary-item__value--high-usage {
  color: var(--warning);
}

.app-summary-item__value--percentage.app-summary-item__value--medium-usage {
  color: var(--warning-light);
}

.app-summary-item__value--percentage.app-summary-item__value--low-usage {
  color: var(--success);
}

/* Progress Items */
.app-progress-item {
  margin-bottom: var(--space-6);
  padding: var(--space-4);
  background: var(--neutral-50);
  border-radius: var(--radius-md);
  border: 1px solid var(--neutral-200);
}

.app-progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-3);
}

.app-progress-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--neutral-700);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.app-progress-percentage {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--neutral-600);
  background: white;
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  border: 1px solid var(--neutral-200);
}

.app-progress-item :deep(.p-progressbar) {
  height: 10px;
  border-radius: var(--radius-sm);
  background: var(--neutral-200);
  overflow: hidden;
}

.app-progress-item :deep(.p-progressbar-value) {
  border-radius: var(--radius-sm);
  transition: all var(--transition-base);
}

.app-progress--over :deep(.p-progressbar-value) {
  background: linear-gradient(90deg, var(--error) 0%, var(--error-dark) 100%);
  box-shadow: 0 0 20px rgba(239, 68, 68, 0.4);
}

.app-progress--high :deep(.p-progressbar-value) {
  background: linear-gradient(90deg, var(--warning) 0%, var(--warning-dark) 100%);
  box-shadow: 0 0 15px rgba(245, 158, 11, 0.3);
}

.app-progress--medium :deep(.p-progressbar-value) {
  background: linear-gradient(90deg, var(--warning-light) 0%, var(--warning) 100%);
  box-shadow: 0 0 15px rgba(251, 191, 36, 0.3);
}

.app-progress--low :deep(.p-progressbar-value) {
  background: linear-gradient(90deg, var(--success) 0%, var(--success-dark) 100%);
  box-shadow: 0 0 15px rgba(16, 185, 129, 0.3);
}

/* Payment Progress Bar Styles */
.app-progress--payment-complete :deep(.p-progressbar-value) {
  background: linear-gradient(90deg, var(--success) 0%, var(--success-dark) 100%);
  box-shadow: 0 0 20px rgba(16, 185, 129, 0.4);
}

.app-progress--payment-high :deep(.p-progressbar-value) {
  background: linear-gradient(90deg, var(--primary) 0%, var(--primary-dark) 100%);
  box-shadow: 0 0 15px rgba(79, 70, 229, 0.3);
}

.app-progress--payment-medium :deep(.p-progressbar-value) {
  background: linear-gradient(90deg, var(--info) 0%, var(--info-dark) 100%);
  box-shadow: 0 0 15px rgba(14, 165, 233, 0.3);
}

.app-progress--payment-low :deep(.p-progressbar-value) {
  background: linear-gradient(90deg, var(--warning-light) 0%, var(--warning) 100%);
  box-shadow: 0 0 15px rgba(251, 191, 36, 0.3);
}

/* Detail Section */
.app-details {
  margin-top: var(--space-8);
}

.app-tabs {
  display: flex;
  gap: var(--space-2);
  margin-bottom: var(--space-6);
  border-bottom: 1px solid var(--neutral-200);
}

.app-tab {
  padding: var(--space-3) var(--space-4);
  background: transparent;
  border: none;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--neutral-600);
  cursor: pointer;
  transition: all var(--transition-base);
  border-bottom: 2px solid transparent;
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
}

.app-tab:hover {
  color: var(--primary);
  background: var(--neutral-50);
}

.app-tab--active {
  color: var(--primary);
  border-bottom-color: var(--primary);
  font-weight: var(--font-weight-semibold);
  background: var(--neutral-50);
}

/* Animations */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.02);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Animation classes */
.app-budget-content {
  animation: fadeIn 0.5s ease-out;
}

.app-section-title {
  animation: slideInUp 0.4s ease-out 0.1s both;
}

.app-details {
  animation: slideInUp 0.6s ease-out 0.2s both;
}

.app-summary-item:nth-child(1) { animation: slideInUp 0.4s ease-out 0.2s both; }
.app-summary-item:nth-child(2) { animation: slideInUp 0.4s ease-out 0.3s both; }
.app-summary-item:nth-child(3) { animation: slideInUp 0.4s ease-out 0.4s both; }
.app-summary-item:nth-child(4) { animation: slideInUp 0.4s ease-out 0.5s both; }

/* Responsive Design */
@media (max-width: 1200px) {
  .app-budget-report__title {
    font-size: var(--font-size-2xl);
  }
}

@media (max-width: 768px) {
  .app-budget-content {
    padding: var(--space-3);
  }

  .app-title {
    font-size: var(--font-size-xl);
  }

  .app-summary-item {
    padding: var(--space-3);
  }

  .app-summary-item__value {
    font-size: var(--font-size-lg);
  }

  .app-loading,
  .app-error {
    padding: var(--space-8);
  }
}

@media (max-width: 480px) {
  .app-budget-content {
    padding: var(--space-2);
  }

  .app-actions {
    flex-direction: column;
    width: 100%;
  }

  .app-actions .app-button {
    width: 100%;
    max-width: none;
    justify-content: center;
  }

  .app-summary-item__value {
    font-size: var(--font-size-base);
  }
}

/* Dark Mode Support */
@media (prefers-color-scheme: dark) {
  .app-title {
    background: linear-gradient(135deg, var(--primary-light) 0%, var(--primary) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
}

/* High Contrast Mode */
@media (prefers-contrast: high) {
  .app-summary-item {
    border: 2px solid var(--neutral-800);
  }

  .app-progress-item {
    border: 2px solid var(--neutral-800);
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .app-budget-content,
  .app-summary-item,
  .app-tab,
  .app-progress-item :deep(.p-progressbar-value),
  .app-section-title,
  .app-details {
    transition: none;
    animation: none;
  }

  .app-summary-item:hover,
  .app-tab:hover {
    transform: none;
  }

  .app-summary-item__value--negative {
    animation: none;
  }

  .app-summary-item:nth-child(1),
  .app-summary-item:nth-child(2),
  .app-summary-item:nth-child(3),
  .app-summary-item:nth-child(4) {
    animation: none;
  }
}
</style>
